import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'

import Snackbars from '../../component/Snackbars'
import API from '../../component/Axios'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import { LIST_FILTER_DATE_FIELD_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { getLocationState } from '../../utils/listPageState'
import { formatSafeNumber as formatNumber } from '../../utils/numberDisplay'

export default function Home({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const locationState = getLocationState(location)
    const initialSearchSelectRef = useRef({
        period: 'day',
        day: new Date(),
        month: new Date(),
    })
    const requestSequenceRef = useRef(0)
    const [loading, setLoading] = useState(true)
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar)
    const [datas, setDatas] = useState([])
    const [searchSelect, setSearchSelect] = useState(initialSearchSelectRef.current)

    const totals = useMemo(() => {
        return datas.reduce((acc, item) => {
            acc.totalVisitCount += Number(item.count) || 0
            acc.totalSignCount += Number(item.signCount) || 0
            acc.totalSignCountWeb += Number(item.signCountWeb) || 0
            acc.totalSignCountAnd += Number(item.signCountAnd) || 0
            acc.totalSignCountIos += Number(item.signCountIos) || 0
            acc.totalSalesAmount += Number(item.salesAmount) || 0
            acc.totalBuyCount += Number(item.buyCount) || 0
            acc.totalBuyCountToday += Number(item.buyCountToday) || 0
            acc.totalBuyCountWeb += Number(item.buyCountWeb) || 0
            acc.totalBuyCountAnd += Number(item.buyCountAnd) || 0
            acc.totalBuyCountIos += Number(item.buyCountIos) || 0
            return acc
        }, {
            totalVisitCount: 0,
            totalSignCount: 0,
            totalSignCountWeb: 0,
            totalSignCountAnd: 0,
            totalSignCountIos: 0,
            totalSalesAmount: 0,
            totalBuyCount: 0,
            totalBuyCountToday: 0,
            totalBuyCountWeb: 0,
            totalBuyCountAnd: 0,
            totalBuyCountIos: 0,
        })
    }, [datas])

    const getData = useCallback(async (currentSearchSelect) => {
        if (!currentSearchSelect || (currentSearchSelect.period === 'day' && !currentSearchSelect.day) || (currentSearchSelect.period === 'month' && !currentSearchSelect.month)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('조회 기간을 선택하세요.')
            return
        }

        const requestId = requestSequenceRef.current + 1
        requestSequenceRef.current = requestId
        setLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/dashboard/getAnalyticsList', {
                adminId,
                role,
                searchSelect: currentSearchSelect,
            })

            if (requestId !== requestSequenceRef.current) {
                return
            }

            if (data.code === 200) {
                setDatas(Array.isArray(data.data) ? data.data : [])
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (err) {
            if (requestId !== requestSequenceRef.current) {
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(err.response?.data?.message || err.message || '통계 조회 중 오류가 발생했습니다.')
        } finally {
            if (requestId === requestSequenceRef.current) {
                setLoading(false)
            }
        }
    }, [adminId, history, role])

    useEffect(() => {
        if (adminId && role === 1) {
            history.push('/sports')
            return
        }

        if (adminId && role === 2) {
            history.push('/appQnas')
            return
        }

        if (adminId && role === 3) {
            history.push('/users')
            return
        }

        if (adminId && role > 3) {
            getData(initialSearchSelectRef.current)
        }
    }, [adminId, getData, history, role])

    const searchButtonClick = () => {
        getData(searchSelect)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        setSearchSelect(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    if (role <= 3) {
        return null
    }

    return (
        <>
            <Grid item xs={12} md={12} lg={12}>
                {loading ? (
                    <Spiner/>
                ) : (
                    <>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                            <ListFilterBar>
                                <ListFilterForm onSubmit={handleSearchSubmit}>
                                    <TextField
                                        label="조회 기준"
                                        select
                                        name="period"
                                        value={searchSelect.period}
                                        onChange={updateSearchSelect}
                                        size="small"
                                        sx={LIST_FILTER_SELECT_FIELD_SX}
                                    >
                                        <MenuItem value='day'>일자별 요약</MenuItem>
                                        <MenuItem value='month'>월별 요약</MenuItem>
                                    </TextField>
                                    <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        {searchSelect.period === 'day' ? (
                                            <DesktopDatePicker
                                                inputFormat="yyyy-MM"
                                                mask={"____-__"}
                                                openTo="month"
                                                views={['year','month']}
                                                minDate={new Date(2021, 0, 1)}
                                                maxDate={new Date()}
                                                value={searchSelect.day}
                                                onChange={d => setSearchSelect(prev => ({
                                                    ...prev,
                                                    day: d
                                                }))}
                                                renderInput={(params) => <TextField required {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} />}
                                            />
                                        ) : (
                                            <DesktopDatePicker
                                                inputFormat="yyyy"
                                                mask={"____"}
                                                openTo="year"
                                                views={['year']}
                                                minDate={new Date(2021, 0, 1)}
                                                maxDate={new Date()}
                                                value={searchSelect.month}
                                                onChange={d => setSearchSelect(prev => ({
                                                    ...prev,
                                                    month: d
                                                }))}
                                                renderInput={(params) => <TextField required {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} />}
                                            />
                                        )}
                                    </LocalizationProvider>
                                    <IconButton type="submit" aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                                </ListFilterForm>
                            </ListFilterBar>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                주문수 및 Web/And/iOS는 결제완료 기준입니다.
                            </Typography>
                            <EmbeddedTableContainer>
                                <Table size="small" sx={{ minWidth: 960 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>일자</TableCell>
                                            <TableCell align="right">가입</TableCell>
                                            <TableCell align="right">Web</TableCell>
                                            <TableCell align="right">And</TableCell>
                                            <TableCell align="right">iOS</TableCell>
                                            <TableCell align="right">방문자</TableCell>
                                            <TableCell align="right">매출액</TableCell>
                                            <TableCell align="right">주문수/신규가입자</TableCell>
                                            <TableCell align="right">Web</TableCell>
                                            <TableCell align="right">And</TableCell>
                                            <TableCell align="right">iOS</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                    {datas && datas.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11}>
                                                <ListEmptyState
                                                    title="조회된 통계 데이터가 없습니다."
                                                    description="조회 기준과 기간을 변경한 뒤 다시 확인해보세요."
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        datas.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{row.date}</TableCell>
                                                <TableCell align="right">{formatNumber(row.signCount)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.signCountWeb)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.signCountAnd)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.signCountIos)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.count)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.salesAmount)}</TableCell>
                                                <TableCell align="right">{`${formatNumber(row.buyCount)} / ${formatNumber(row.buyCountToday)}`}</TableCell>
                                                <TableCell align="right">{formatNumber(row.buyCountWeb)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.buyCountAnd)}</TableCell>
                                                <TableCell align="right">{formatNumber(row.buyCountIos)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                        <TableRow>
                                            <TableCell><b>합계</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalSignCount)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalSignCountWeb)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalSignCountAnd)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalSignCountIos)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalVisitCount)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalSalesAmount)}</b></TableCell>
                                            <TableCell align="right"><b>{`${formatNumber(totals.totalBuyCount)} / ${formatNumber(totals.totalBuyCountToday)}`}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalBuyCountWeb)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalBuyCountAnd)}</b></TableCell>
                                            <TableCell align="right"><b>{formatNumber(totals.totalBuyCountIos)}</b></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </EmbeddedTableContainer>
                        </Paper>
                    </>
                )}
            </Grid>
            {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
        </>
    )
}
